import { useId, useState } from "react"

import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import {
  type AgentDeliveryReceipt,
  type AgentUndeliveredResult,
  listAgentDeliveries,
  retryAgentDelivery,
} from "@/api/client/hosted-agent-deliveries"
import { Info } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { formatFriendlyDateTime } from "@/lib/dates"
import { cn } from "@/lib/utils"

import {
  AGENT_RAIL_HEADER_CLASS,
  AGENT_RAIL_LABEL_CLASS,
  AGENT_RAIL_META_CLASS,
  AGENT_RAIL_SECTION_CLASS,
  AGENT_STATUS_DOT_CLASS,
  deliveryStateTone,
} from "../utils/agents-utils"

export function AgentDeliveryReceipts({ agentId }: { agentId: string }) {
  // Pagination and confirmation state must never carry into another agent.
  return <AgentDeliveryReceiptList key={agentId} agentId={agentId} />
}

function AgentDeliveryReceiptList({ agentId }: { agentId: string }) {
  const { t } = useTranslation()

  const client = useQueryClient()
  const resultCodeDescriptions: Record<string, string> = {
    no_current_destinations: t("agents.deliveries.result_codes.no_current_destinations", {
      defaultValue: "No approved delivery destinations remain for this report.",
    }),
    recovery_window_expired: t("agents.deliveries.result_codes.recovery_expired", {
      defaultValue:
        "Automatic recovery expired before all deliveries were prepared. Review the receipts before running the report again.",
    }),
    delivery_policy_gone: t("agents.deliveries.result_codes.delivery_policy_gone", {
      defaultValue: "Delivery permission was removed or changed.",
    }),
    recovery_attempts_exhausted: t("agents.deliveries.result_codes.recovery_attempts_exhausted", {
      defaultValue:
        "Automatic recovery could not complete delivery setup. Contact an administrator.",
    }),
    intent_unreadable: t("agents.deliveries.result_codes.intent_unreadable", {
      defaultValue: "Saved delivery settings could not be read. Contact an administrator.",
    }),
    content_withdrawn: t("agents.deliveries.content_withdrawn", {
      defaultValue: "Content was withdrawn. This delivery cannot be sent again.",
    }),
  }

  // Provider acceptance is not proof of receipt. The sentence is permanent
  // information, so it is a disclosure behind the section's (i) — not a
  // standing paragraph, and not an `aria-label` on a no-op button: a label
  // that long is read as a control's name, announced only on focus and
  // unreadable for anyone who is not using a screen reader.
  const acceptanceNote = t("agents.deliveries.acceptance_note", {
    defaultValue:
      "Provider acceptance does not confirm that a recipient received or read the report.",
  })
  const acceptanceNoteId = useId()
  const [isAcceptanceNoteOpen, setAcceptanceNoteOpen] = useState(false)

  const key = ["agent-deliveries", agentId, "pages"]

  const query = useInfiniteQuery({
    queryKey: key,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => listAgentDeliveries(agentId, { cursor: pageParam, signal }),
    getNextPageParam: (page, _pages, pageParam) =>
      page.truncated && page.next_cursor && page.next_cursor !== pageParam
        ? page.next_cursor
        : undefined,
    retry: false,
    refetchInterval: 15000,
  })

  // Offset pages can overlap while new receipts arrive. Keep one row per id.
  const uniqueReceipts = new Map<string, AgentDeliveryReceipt>()
  const uniqueUndeliveredResults = new Map<string, AgentUndeliveredResult>()
  for (const page of query.data?.pages ?? []) {
    for (const result of page.undelivered_results ?? []) {
      if (!uniqueUndeliveredResults.has(result.result_id))
        uniqueUndeliveredResults.set(result.result_id, result)
    }
    for (const receipt of page.deliveries) {
      if (!uniqueReceipts.has(receipt.id)) uniqueReceipts.set(receipt.id, receipt)
    }
  }
  const receipts = [...uniqueReceipts.values()]
  const undeliveredResults = [...uniqueUndeliveredResults.values()]
  const lastPage = query.data?.pages.at(-1)
  const incomplete = lastPage?.truncated && !query.hasNextPage
  const [uncertainId, setUncertainId] = useState<string | null>(null)
  const uncertain =
    receipts.find(
      (receipt) =>
        receipt.id === uncertainId &&
        receipt.state === "uncertain" &&
        receipt.code !== "content_withdrawn"
    ) ?? null

  const retry = useMutation({
    mutationFn: ({
      receipt,
      acknowledge,
    }: {
      receipt: AgentDeliveryReceipt
      acknowledge: boolean
    }) => retryAgentDelivery(agentId, receipt.id, acknowledge),
    onSuccess: () => {
      setUncertainId(null)
      void client.invalidateQueries({ queryKey: key })
    },
  })

  // The section is a rail section, not a standing card: it renders only once
  // this agent actually has delivery history, or when the status could not be
  // read at all (an unreadable status must never look like "nothing was
  // sent"). Agents that never deliver keep the rail clear.
  const hasDeliveryHistory = receipts.length > 0 || undeliveredResults.length > 0
  if (!query.isError && !hasDeliveryHistory) return null

  return (
    <section
      aria-label={t("agents.deliveries.title", { defaultValue: "Deliveries" })}
      className={AGENT_RAIL_SECTION_CLASS}
      data-testid="agent-deliveries-section"
    >
      <div className={AGENT_RAIL_HEADER_CLASS}>
        <span className={AGENT_RAIL_LABEL_CLASS}>
          {t("agents.deliveries.title", { defaultValue: "Deliveries" })}
        </span>
        {/* A real disclosure: the (i) toggles the sentence below the label, so
            it is readable by keyboard, by pointer and on touch alike. The
            paragraph always exists (only `hidden`), so `aria-controls` always
            resolves to a node. */}
        <Button
          aria-controls={acceptanceNoteId}
          aria-expanded={isAcceptanceNoteOpen}
          className="size-6"
          size="icon"
          type="button"
          variant="ghost"
          aria-label={t("agents.deliveries.acceptance_note_label", {
            defaultValue: "About delivery status",
          })}
          onClick={() => setAcceptanceNoteOpen((open) => !open)}
        >
          <Info aria-hidden="true" className="size-3.5" />
        </Button>
      </div>

      <p
        className="text-muted-foreground px-1 text-xs"
        hidden={!isAcceptanceNoteOpen}
        id={acceptanceNoteId}
      >
        {acceptanceNote}
      </p>

      {query.isError ? (
        <p className="text-muted-foreground text-sm" role="alert">
          {t("agents.deliveries.unavailable", { defaultValue: "Delivery status is unavailable." })}
        </p>
      ) : null}

      {retry.isError ? (
        <p className="text-destructive text-sm" role="alert">
          {t("agents.deliveries.retry_failed", {
            defaultValue: "The retry was refused or failed. Refresh status before trying again.",
          })}
        </p>
      ) : null}

      <ul className="flex max-h-72 flex-col gap-1.5 overflow-y-auto">
        {undeliveredResults.map((result) => (
          <li
            key={`result:${result.result_id}`}
            className="bg-card flex flex-col gap-1 rounded-md border px-3 py-2.5"
            data-testid="agent-delivery-row"
          >
            <span className="line-clamp-2 text-[13px] leading-snug font-semibold break-words">
              {result.title}
            </span>
            <span className={cn(AGENT_RAIL_META_CLASS, "flex items-center gap-1.5")}>
              <span
                aria-hidden="true"
                className={cn(
                  "size-2 shrink-0 rounded-full",
                  AGENT_STATUS_DOT_CLASS[deliveryStateTone(result.state)]
                )}
              />
              <span className="truncate" role="status">
                {result.state === "pending_enqueue"
                  ? t("agents.deliveries.pending_enqueue", {
                      defaultValue: "Delivery setup is incomplete. Recovery is pending.",
                    })
                  : t("agents.deliveries.cancelled_before_delivery", {
                      defaultValue:
                        "Delivery setup was cancelled. Review any delivery receipts below.",
                    })}
              </span>
            </span>
            <span className={AGENT_RAIL_META_CLASS}>
              {formatFriendlyDateTime(result.updated_at)}
            </span>
            {result.code ? (
              <span className={cn(AGENT_RAIL_META_CLASS, "break-words")}>
                {resultCodeDescriptions[result.code] ?? result.code}
              </span>
            ) : null}
            <Link
              className="text-[11px] underline underline-offset-2"
              to={`/agents/${encodeURIComponent(agentId)}?artifact=${encodeURIComponent(result.artifact_id)}`}
            >
              {t("agents.deliveries.open_saved_result", { defaultValue: "Open saved report" })}
            </Link>
          </li>
        ))}
        {receipts.map((receipt) => (
          <li
            key={receipt.id}
            className="bg-card flex flex-col gap-1 rounded-md border px-3 py-2.5"
            data-testid="agent-delivery-row"
          >
            <span className="line-clamp-2 text-[13px] leading-snug font-semibold break-words">
              {receipt.channel === "email" ? "Email" : "Teams"} ·{" "}
              {receipt.destination_name || receipt.destination_id}
            </span>
            <span className={cn(AGENT_RAIL_META_CLASS, "flex items-center gap-1.5")}>
              <span
                aria-hidden="true"
                data-delivery-tone={deliveryStateTone(receipt.state)}
                className={cn(
                  "size-2 shrink-0 rounded-full",
                  AGENT_STATUS_DOT_CLASS[deliveryStateTone(receipt.state)]
                )}
              />
              <span className="truncate" role="status">
                {t(`agents.deliveries.states.${receipt.state}`, { defaultValue: receipt.state })}
              </span>
              <span aria-hidden="true">·</span>
              <span className="truncate">{formatFriendlyDateTime(receipt.updated_at)}</span>
            </span>

            {receipt.code ? (
              <span className={cn(AGENT_RAIL_META_CLASS, "break-words")}>
                {receipt.code === "content_withdrawn"
                  ? t("agents.deliveries.content_withdrawn", {
                      defaultValue: "Content was withdrawn. This delivery cannot be sent again.",
                    })
                  : receipt.code}
              </span>
            ) : null}

            <Link
              className="text-[11px] underline underline-offset-2"
              to={`/agents/${encodeURIComponent(agentId)}?artifact=${encodeURIComponent(receipt.artifact_id)}`}
            >
              {t("agents.artifacts.version", { version: receipt.artifact_version })}
            </Link>

            {receipt.code !== "content_withdrawn" &&
            (receipt.state === "failed" || receipt.state === "uncertain") ? (
              <Button
                className="h-7 self-start"
                disabled={retry.isPending}
                size="sm"
                type="button"
                variant="outline"
                onClick={() =>
                  receipt.state === "uncertain"
                    ? setUncertainId(receipt.id)
                    : retry.mutate({ receipt, acknowledge: false })
                }
              >
                {t("agents.deliveries.retry", { defaultValue: "Retry delivery" })}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>

      {query.hasNextPage ? (
        <Button
          className="h-7 self-start px-0"
          disabled={query.isFetching}
          size="sm"
          type="button"
          variant="link"
          onClick={() => void query.fetchNextPage({ cancelRefetch: false })}
        >
          {query.isFetchingNextPage
            ? t("agents.deliveries.loading_more", { defaultValue: "Loading older deliveries…" })
            : t("agents.deliveries.load_more", { defaultValue: "Load older deliveries" })}
        </Button>
      ) : null}
      {query.isFetchingNextPage ? (
        <p className={AGENT_RAIL_META_CLASS} role="status">
          {t("agents.deliveries.loading_more", { defaultValue: "Loading older deliveries…" })}
        </p>
      ) : null}
      {incomplete || query.isFetchNextPageError ? (
        <p className="text-muted-foreground text-sm" role="alert">
          {t("agents.deliveries.history_incomplete", {
            defaultValue:
              "More delivery history is available, but could not be loaded. Refresh and try again.",
          })}
        </p>
      ) : null}

      <Button
        className="h-7 self-start px-0"
        disabled={query.isFetching}
        size="sm"
        type="button"
        variant="link"
        onClick={() => void query.refetch()}
      >
        {t("agents.deliveries.refresh", { defaultValue: "Refresh deliveries" })}
      </Button>

      <AlertDialog
        open={!!uncertain}
        onOpenChange={(open) => {
          if (!open && !retry.isPending) setUncertainId(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("agents.deliveries.uncertain_title", { defaultValue: "Send another copy?" })}
            </AlertDialogTitle>

            <AlertDialogDescription>
              {t("agents.deliveries.uncertain_warning", {
                defaultValue:
                  "The provider may already have accepted this delivery. Retrying may send a duplicate to the recipient.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={retry.isPending}>
              {t("agents.deliveries.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>

            <AlertDialogAction
              disabled={retry.isPending}
              onClick={(event) => {
                event.preventDefault()
                if (uncertain) retry.mutate({ receipt: uncertain, acknowledge: true })
              }}
            >
              {t("agents.deliveries.confirm_duplicate", {
                defaultValue: "Accept duplicate risk and retry",
              })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
